import { makeError } from './exception';
import type { Fraction } from './fractionType';
import { invariant } from './invariant';
import { makeValidationFunction, validateNumberProperty } from './validation';

/**
 * Find the smallest positive integer that is divisible by both a and b.
 *
 * Reference:
 * https://en.wikipedia.org/wiki/Least_common_multiple
 */
export function leastCommonMultiple(a: number, b: number) {
  invariant(a >= 0, 'LCM, negative not supported');
  invariant(b >= 0, 'LCM, negative not supported');
  return (a * b) / greatestCommonDivisor(a, b);
}

/**
 * Find the largest positive integer that divides each of the given integers.
 *
 * Reference:
 * https://en.wikipedia.org/wiki/Greatest_common_divisor
 */
export function greatestCommonDivisor(x: number, y: number) {
  x = Math.abs(x);
  y = Math.abs(y);
  while (y) {
    [x, y] = [y, x % y];
  }
  return x;
}


/**
 * Given a string of the form "50/1", "50:1" or "50", returns a Fraction object.
 */
export function parseFraction(frac: string): Fraction {
  const separator = frac.includes(':') ? ':' : '/';
  const fpsParts: string[] = frac.split(separator);
  const numerator = parseInt(fpsParts[0], 10);
  if (fpsParts.length === 1) {
    if (isNaN(numerator) || (numerator.toString() !== fpsParts[0])) {
      throw Error('Non-integer and non-fraction string passed to parseFraction');
    }
    return { numerator, denominator: 1 };
  }
  if (fpsParts.length !== 2) {
    throw Error('Fraction must have single / or :');
  }
  const denominator = parseInt(fpsParts[1], 10);
  if (isNaN(numerator) || isNaN(denominator) || (denominator.toString() != fpsParts[1])) {
    throw Error('Non-integer numerator or denominator');
  }

  return { numerator, denominator };
}

/**
 * Same as above except it accepts undefined and may return undefined. If the input
 * type is not a undefined and not a string the function will raise an exception
 */
export function maybeParseFraction(frac: unknown | undefined): Fraction | undefined {
  if (frac === undefined) {
    return undefined;
  }
  if (typeof frac !== 'string') {
    throw Error('Non-string input passed to maybeParseFraction');
  }
  return parseFraction(frac);
}

export function invertFraction(frac: Fraction) {
  return {
    numerator: frac.denominator,
    denominator: frac.numerator
  };
}

export function simplifyFraction(frac: Fraction): Fraction {
  const gcd = greatestCommonDivisor(frac.numerator, frac.denominator);
  return {
    numerator: frac.numerator / gcd,
    denominator: frac.denominator / gcd
  };
}

export function divideFractionBy(frac: Fraction, divisor: number) {
  return simplifyFraction({ numerator: frac.numerator, denominator: frac.denominator * divisor});
}

export function multiplyFractionBy(frac: Fraction, factor: number) {
  return simplifyFraction({ numerator: frac.numerator * factor, denominator: frac.denominator});
}

export function multiplyFractions(a: Fraction, b: Fraction) {
  return simplifyFraction({ numerator: a.numerator * b.numerator, denominator: a.denominator * b.denominator });
}

export function divideFractions(a: Fraction, b: Fraction) {
  return multiplyFractions(a, invertFraction(b));
}

export function addFractions(a: Fraction, b: Fraction) {
  return simplifyFraction({
    numerator: a.numerator * b.denominator + b.numerator * a.denominator,
    denominator: a.denominator * b.denominator
  });
}

export function ceilFraction(a: Fraction) {
  return Math.ceil(approximateFractionValue(a));
}

export function floorFraction(a: Fraction) {
  return Math.floor(approximateFractionValue(a));
}

export function roundFraction(a: Fraction) {
  return Math.round(approximateFractionValue(a));
}

export function subtractFractions(a: Fraction, b: Fraction) {
  return addFractions(a, multiplyFractionBy(b, -1));
}

export function lessThanFraction(a: Fraction, b: Fraction) {
  return compareFractions(a, b) < 0;
}

export function greaterThanFraction(a: Fraction, b: Fraction) {
  return compareFractions(a, b) > 0;
}

export function compareFractions(a: Fraction, b: Fraction) {
  return subtractFractions(a, b).numerator;
}

export function maxFractions(a: Fraction, b: Fraction) {
  return (compareFractions(a, b) < 0) ? b : a;
}

export function minFractions(a: Fraction, b: Fraction) {
  return (compareFractions(a, b) > 0) ? b : a;
}

export function absFraction(a: Fraction): Fraction {
  return {
    numerator: Math.abs(a.numerator),
    denominator: a.denominator
  };
}


export function minimumFraction(first: Fraction, ...other: Fraction[]) {
  let result = first;
  for (const candidate of other) {
    if (lessThanFraction(candidate, result)) {
      result = candidate;
    }
  }
  return result;
}

/**
 * Calculate floating point number from fraction. This is a potentially lossy
 * operation, since not all fractions can be expressed accurately as floating
 * point.
 */
export function approximateFractionValue(frac: Fraction) {
  return frac.numerator / frac.denominator;
}


/**
 * Returns a fraction that is an approximation of the given number.
 */
export function approximateNumber(n: number): Fraction {
  const denominator = 10000;
  const numerator = Math.round(n * denominator);
  return simplifyFraction({ numerator, denominator });
}


/**
 * Make string from duration expressed as fraction, suitable for expression
 * evaluators such as the ffmpeg-utils expression evaluator.
 *
 * References:
 * https://ffmpeg.org/ffmpeg-utils.html#Expression-Evaluation
 */
export function formatFractionAsExpression(frac: Fraction): string {
  if (frac.denominator == 1) {
    return '' + frac.numerator;
  }
  return frac.numerator + '/' + frac.denominator;
}

/**
 * Rewrite a fraction to have a different demoninator, then return the new
 * numerator of that new fraction.
 *
 * Use this when you need to express a value using a predetermined denominator,
 * such as how time units are frequently expressed using a time scale defined by
 * the media format.
 */
export function getNumeratorForDenominator(
  frac: Fraction,
  denominator: number
): number {
  if (frac.denominator > denominator) {
    frac = simplifyFraction(frac);
  }

  if (frac.denominator > denominator || denominator % frac.denominator != 0) {
    throw makeError(
      'Denominator does not result in integer numerator',
      'BadIntegerMath',
      { info: { frac, denominator } }
    );
  }

  const factor = denominator / frac.denominator;
  invariant(Number.isInteger(factor), 'Expected integer factor');

  return frac.numerator * factor;
}

export const validateFraction =
  makeValidationFunction<Fraction>((u) => {
    return {
      numerator: validateNumberProperty(u, 'numerator'),
      denominator: validateNumberProperty(u, 'denominator'),
    };
  });

export function frac(numerator: number, denominator: number): Fraction {
  return { numerator, denominator };
}

export function clampFraction(value: Fraction, min: Fraction, max: Fraction): Fraction {
  if (compareFractions(value, min) < 0) {
    return min;
  }
  if (compareFractions(value, max) > 0) {
    return max;
  }
  return value;
}

/**
 * Creates a new fraction representing zero (0/1).
 */
export function zeroFrac(): Fraction {
  return { numerator: 0, denominator: 1 };
}

/**
 * Creates a new fraction representing one half (1/2).
 */
export function halfFrac(): Fraction {
  return { numerator: 1, denominator: 2 };
}

/**
 * Creates a new fraction representing one (1/1).
 */
export function oneFrac(): Fraction {
  return { numerator: 1, denominator: 1 };
}

